"use client";

import React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus } from "lucide-react";
import { ListItemRow } from "./ListItemRow";
import type { ListEditorContentProps } from "./types";

/**
 * Modal body for the ListFieldEditor.
 * Renders validation errors, the scrollable items list, the add-item form,
 * and the save/cancel action buttons.
 */
export function ListEditorContent({
  items,
  newItem,
  validationErrors,
  editingIndex,
  editingValue,
  isSaving,
  placeholder,
  maxItems,
  fieldLabel,
  onNewItemChange,
  onAddItem,
  onRemoveItem,
  onStartEdit,
  onSaveEdit,
  onCancelEdit,
  onEditingValueChange,
  onKeyDown,
  onSave,
  onCancel,
  onClose,
}: ListEditorContentProps) {
  const isAtMaxItems = maxItems !== undefined && items.length >= maxItems;
  const hasValidationErrors = Object.keys(validationErrors).length > 0;

  return (
    <div className="space-y-4">
      {/* Validation Errors */}
      {hasValidationErrors && (
        <div className="bg-destructive/10 border border-destructive/30 rounded-md p-3">
          <p className="text-sm font-semibold text-destructive mb-2">Validation Errors:</p>
          <ul className="space-y-1">
            {Object.entries(validationErrors).map(([key, error]) => (
              <li key={key} className="text-xs text-destructive">
                • {error}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Items List */}
      <div className="space-y-2">
        <p className="text-sm font-semibold">
          Items ({items.length}
          {maxItems ? `/${maxItems}` : ""})
        </p>
        {items.length === 0 ? (
          <div className="text-sm text-muted-foreground italic p-3 bg-muted rounded-md border border-dashed border-input">
            No items yet
          </div>
        ) : (
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {items.map((item, index) => (
              <ListItemRow
                key={index}
                item={item}
                index={index}
                isEditing={editingIndex === index}
                editingValue={editingValue}
                placeholder={placeholder}
                onStartEdit={onStartEdit}
                onRemove={onRemoveItem}
                onSaveEdit={onSaveEdit}
                onCancelEdit={onCancelEdit}
                onEditingValueChange={onEditingValueChange}
                onKeyDown={onKeyDown}
              />
            ))}
          </div>
        )}
      </div>

      {/* Add New Item Form */}
      {editingIndex === null && (
        <div className="space-y-2 pt-2 border-t">
          <p className="text-sm font-semibold">Add Item</p>
          <div className="flex flex-col sm:flex-row gap-2">
            <Input
              value={newItem}
              onChange={(e) => onNewItemChange(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder={placeholder}
              disabled={isSaving || isAtMaxItems}
              className="flex-1 h-10 sm:h-9"
            />
            <Button
              onClick={onAddItem}
              disabled={isSaving || !newItem.trim() || isAtMaxItems}
              className="gap-1 h-10 sm:h-9 w-full sm:w-auto"
            >
              <Plus className="h-4 w-4" />
              Add
            </Button>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-end pt-2 border-t">
        <Button
          variant="outline"
          onClick={() => {
            onCancel();
            onClose();
          }}
          disabled={isSaving}
          className="h-10 sm:h-9 w-full sm:w-auto"
        >
          Cancel
        </Button>
        <Button
          onClick={() => onSave(onClose)}
          disabled={isSaving || hasValidationErrors}
          className="h-10 sm:h-9 w-full sm:w-auto"
          title={hasValidationErrors ? "Fix validation errors before saving" : ""}
        >
          {isSaving ? "Saving..." : "Save"}
        </Button>
      </div>
    </div>
  );
}
